/* L'identité d'un enregistrement : lue, puis modifiée au même endroit.
 *
 * Une fiche client, usine, produit ou agent, c'est quarante champs rangés en
 * groupes. Les écrire quatre fois donnerait quatre fiches qui divergent au
 * premier ajout. On les décrit donc en données, et ce composant les rend —
 * en lecture d'abord, en saisie quand on le demande.
 *
 * Il ne sait rien du métier : il reçoit des champs, rend ce qui a changé, et
 * c'est l'appelant qui écrit en base et inscrit la trace.
 */

import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Icon } from "./Icon";
import { Section } from "./primitives";
import { dateFr, euro, pourcent } from "@/lib/format";

export type TypeChamp =
  | "texte" | "zone" | "mono" | "nombre" | "euro" | "pct" | "date"
  | "select" | "bool" | "tel" | "mail" | "url";

export interface ChampDef<T> {
  cle: keyof T & string;
  nom: string;
  type?: TypeChamp;
  /** Pour un `select`. La valeur vide est toujours proposée. */
  options?: readonly { v: string; n: string }[];
  /** Une ligne pour lui seul : adresses, notes, listes d'ingrédients. */
  large?: boolean;
  aide?: string;
  /** Rendu en lecture, quand la valeur brute ne se lit pas telle quelle. */
  rendu?: (v: T) => ReactNode;
  /** Jamais modifiable, même quand la fiche l'est : une référence, un code. */
  fige?: boolean;
  unite?: string;
}

export interface GroupeDef<T> {
  titre: string;
  note?: string;
  champs: readonly ChampDef<T>[];
}

function lire<T>(v: T, c: ChampDef<T>): ReactNode {
  if (c.rendu) return c.rendu(v);
  const brut = (v as Record<string, unknown>)[c.cle];
  if (c.type === "bool") return brut ? "oui" : "non";
  if (brut === null || brut === undefined || brut === "") return "—";
  switch (c.type) {
    case "euro":
      return <span className="mono">{euro(Number(brut))}</span>;
    case "pct":
      return <span className="mono">{pourcent(Number(brut))}</span>;
    case "date":
      return <span className="mono">{dateFr(String(brut))}</span>;
    case "nombre":
      return (
        <span className="mono">
          {String(brut)}
          {c.unite ? ` ${c.unite}` : ""}
        </span>
      );
    case "mono":
      return <span className="mono">{String(brut)}</span>;
    case "mail":
      return <a href={`mailto:${String(brut)}`}>{String(brut)}</a>;
    case "tel":
      return <a href={`tel:${String(brut).replace(/\s/g, "")}`}>{String(brut)}</a>;
    case "url": {
      const u = String(brut);
      return (
        <a href={u.startsWith("http") ? u : `https://${u}`} target="_blank" rel="noreferrer">
          {u.replace(/^https?:\/\//, "")}
        </a>
      );
    }
    case "select": {
      const o = c.options?.find((x) => x.v === String(brut));
      return o ? o.n : String(brut);
    }
    default:
      return String(brut);
  }
}

/** Ce que le formulaire rend : des chaînes, qu'on reconvertit au type du champ. */
function convertir<T>(c: ChampDef<T>, s: string): unknown {
  if (c.type === "bool") return s === "oui";
  if (s === "") return null;
  if (c.type === "nombre" || c.type === "euro" || c.type === "pct") {
    const n = Number(s.replace(",", "."));
    return Number.isFinite(n) ? n : null;
  }
  return s;
}

function texteDe<T>(v: T, c: ChampDef<T>): string {
  const brut = (v as Record<string, unknown>)[c.cle];
  if (c.type === "bool") return brut ? "oui" : "non";
  if (brut === null || brut === undefined) return "";
  if (c.type === "date") return String(brut).slice(0, 10);
  return String(brut);
}

export function Identite<T extends { id: string }>({
  valeur, groupes, modifiable, enregistrer, droite,
}: {
  valeur: T;
  groupes: readonly GroupeDef<T>[];
  modifiable: boolean;
  /** Reçoit uniquement les champs modifiés. Lève pour afficher une erreur. */
  enregistrer: (champs: Partial<T>) => Promise<void>;
  droite?: ReactNode;
}) {
  const [edition, setEdition] = useState(false);
  const [brouillon, setBrouillon] = useState<Record<string, string>>({});
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const tous = useMemo(() => groupes.flatMap((g) => g.champs), [groupes]);

  const ouvrir = () => {
    const d: Record<string, string> = {};
    for (const c of tous) d[c.cle] = texteDe(valeur, c);
    setBrouillon(d);
    setErreur(null);
    setEdition(true);
  };

  const poser = async () => {
    const champs: Record<string, unknown> = {};
    for (const c of tous) {
      if (c.fige) continue;
      const avant = texteDe(valeur, c);
      const apres = brouillon[c.cle] ?? "";
      if (avant === apres) continue;
      champs[c.cle] = convertir(c, apres);
    }
    if (!Object.keys(champs).length) {
      setEdition(false);
      return;
    }
    setOccupe(true);
    setErreur(null);
    try {
      await enregistrer(champs as Partial<T>);
      setEdition(false);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : "Enregistrement refusé.");
    } finally {
      setOccupe(false);
    }
  };

  const barre = (
    <div className="fi-outils">
      {droite}
      {modifiable && !edition ? (
        <button type="button" className="btn btn-sm" onClick={ouvrir}>
          <Icon n="crayon" cls="" />
          Modifier
        </button>
      ) : null}
      {edition ? (
        <>
          <button type="button" className="btn btn-sm" onClick={() => setEdition(false)}>
            Annuler
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={occupe}
            onClick={() => void poser()}
          >
            {occupe ? "Enregistrement…" : "Enregistrer"}
          </button>
        </>
      ) : null}
    </div>
  );

  return (
    <>
      {erreur ? <div className="banner">{erreur}</div> : null}
      {groupes.map((g, i) => (
        <Section key={g.titre} titre={g.titre} note={g.note} droite={i === 0 ? barre : undefined}>
          <div className="card card-pad">
            {edition ? (
              <div className="fi-form">
                {g.champs.map((c) => (
                  <Champ
                    key={c.cle}
                    c={c}
                    v={brouillon[c.cle] ?? ""}
                    onChange={(s) => setBrouillon((b) => ({ ...b, [c.cle]: s }))}
                  />
                ))}
              </div>
            ) : (
              <dl className="kv">
                {g.champs.map((c) => (
                  <Lecture key={c.cle} c={c} valeur={valeur} />
                ))}
              </dl>
            )}
          </div>
        </Section>
      ))}
    </>
  );
}

function Lecture<T>({ c, valeur }: { c: ChampDef<T>; valeur: T }) {
  return (
    <>
      <dt>{c.nom}</dt>
      <dd className={c.large ? "kv-large" : undefined}>{lire(valeur, c)}</dd>
    </>
  );
}

function Champ<T>({
  c, v, onChange,
}: { c: ChampDef<T>; v: string; onChange: (s: string) => void }) {
  if (c.fige) {
    return (
      <div className={c.large ? "champ fi-large" : "champ"}>
        <label htmlFor={`f-${c.cle}`}>{c.nom}</label>
        <input id={`f-${c.cle}`} value={v} readOnly disabled />
      </div>
    );
  }
  return (
    <div className={c.large || c.type === "zone" ? "champ fi-large" : "champ"}>
      <label htmlFor={`f-${c.cle}`}>{c.nom}</label>
      {c.type === "zone" ? (
        <textarea id={`f-${c.cle}`} value={v} onChange={(e) => onChange(e.target.value)} />
      ) : c.type === "select" ? (
        <select id={`f-${c.cle}`} value={v} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {/* Une valeur déjà posée qui ne figure pas dans la liste reste
              proposée : sans cela le champ s'afficherait vide et le premier
              enregistrement l'effacerait sans que personne l'ait demandé. */}
          {v && !(c.options ?? []).some((o) => o.v === v) ? (
            <option value={v}>{v} (hors liste)</option>
          ) : null}
          {(c.options ?? []).map((o) => (
            <option key={o.v} value={o.v}>{o.n}</option>
          ))}
        </select>
      ) : c.type === "bool" ? (
        <select id={`f-${c.cle}`} value={v} onChange={(e) => onChange(e.target.value)}>
          <option value="oui">oui</option>
          <option value="non">non</option>
        </select>
      ) : (
        <input
          id={`f-${c.cle}`}
          type={
            c.type === "date" ? "date"
              : c.type === "mail" ? "email"
                : c.type === "tel" ? "tel"
                  : c.type === "nombre" || c.type === "euro" || c.type === "pct" ? "number"
                    : "text"
          }
          step={c.type === "euro" || c.type === "pct" ? "0.01" : undefined}
          value={v}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {c.aide ? <div className="champ-aide">{c.aide}</div> : null}
    </div>
  );
}
